// Pure rekenlogica voor de Metering-pagina (Lynx GetMeterdataGSRN).
//
// Wat de API levert, en waarom hier zoveel regels nodig zijn (gemeten 2026-07-30):
//
// 1. EENHEDEN LOPEN DOOR ELKAAR. Eén GSRN levert in dezelfde periode o.a.:
//      KWH / ActiveConsumption / High+Low        → maandstanden (energie)
//      KWT / ActiveConsumption / TotalHours      → DAGrijen met 96 kwartierwaarden
//      KWT / ConsumedPower, KWT / AccessCapacity → maandelijkse vermogenspiek/capaciteit
//      KVR, K3 / Capacitive-, Inductive-, ReactiveConsumption → reactief
//    kW en kWh zijn verschillende grootheden en mogen NOOIT samen opgeteld worden;
//    reactief (kVAr) en capaciteit horen bij een ander dataset dan verbruik.
//
// 2. HET KWARTIERPROFIEL STAAT IN kW. Een kwartierwaarde is het gemiddelde
//    vermogen over dat kwartier, dus energie = kW × 0,25 h. Vandaar ÷4 → kWh.
//    Kruiscontrole op echte data (GSRN …013986, juni 2025): het kW-profiel ÷4 komt
//    uit op ~91 kWh/dag, terwijl de onafhankelijk aangeleverde maandstanden in KWH
//    op ~97 kWh/dag uitkomen. Zelfde orde van grootte → ÷4 is de juiste factor
//    (zonder de deling zou het profiel er vier keer te hoog uitkomen).
//
// 3. "Rectified" MARKEERT DE VEROUDERDE RIJ, niet de correctie. Elke Rectified-rij
//    heeft een tegenhanger met identieke (van, tot, methode, timeframe, eenheid) en
//    haar validFromUTC is ouder (gemeten: 156/156 gevallen). De geldige waarde is
//    dus die met de NIEUWSTE validFromUTC — in de praktijk de Default-rij.
//
// 4. MAANDSTAND EN KWARTIERPROFIEL BESCHRIJVEN DEZELFDE ENERGIE. Ze samen optellen
//    zou dubbeltellen; de fijnste bron wint en de rest wordt overgeslagen (en
//    geteld, zodat de UI erover kan rapporteren).

// ── Eenheden ────────────────────────────────────────────────────────────────────
// Alleen codes die we echt in de data gezien hebben krijgen een label; een
// onbekende code tonen we ongewijzigd (nog altijd een eenheid bij het getal) in
// plaats van er een te verzinnen.
export const MTR_UNITS = {
  KWH: { label: 'kWh', kind: 'energy' },
  KWT: { label: 'kW', kind: 'power' },
  KW: { label: 'kW', kind: 'power' },
  KVR: { label: 'kVAr', kind: 'reactive' },
  K3: { label: 'K3', kind: 'reactive' },
};

// Een kwartier is een kwart uur: 1 kW gedurende een kwartier = 0,25 kWh.
export const QUARTER_HOURS = 0.25;

export function unitInfo(code) {
  const key = String(code || '').toUpperCase();
  const known = MTR_UNITS[key];
  return {
    code: key || '—',
    label: known ? known.label : (key || '—'),
    kind: known ? known.kind : 'unknown',
    isPower: !!known && known.kind === 'power',
  };
}

// Waarde in kWh voor een vermogenseenheid (kW/kWt): ÷4. Andere eenheden blijven
// ongemoeid — reactief vermogen reken je niet naar kWh om.
export function toKwh(quantity, code) {
  const u = unitInfo(code);
  const q = Number(quantity) || 0;
  if (u.isPower) return { value: q * QUARTER_HOURS, converted: true, from: u.label };
  if (u.kind === 'energy') return { value: q, converted: false, from: u.label };
  return { value: null, converted: false, from: u.label };   // niet omrekenbaar
}

export const mtrFmtNum = (v, max = 3) => Number(v || 0).toLocaleString('nl-BE', { maximumFractionDigits: max });

// Korte, leesbare naam van een dataset voor legende en kengetallen. De eenheid staat
// erbij, met "kW÷4" waar we vermogen naar kWh omrekenen — zodat uit de naam zelf
// blijkt dat twee reeksen niet dezelfde bron hebben.
export const METHOD_LABELS = { ActiveConsumption: 'verbruik', ActiveProduction: 'productie' };
export const datasetLabel = (d) => {
  const u = unitInfo(d.unit);
  return `${d.category} · ${METHOD_LABELS[d.method] || d.method} · ${u.isPower ? 'kW÷4' : u.label}`;
};

// Getal MET eenheid — nooit een kaal getal in de UI.
export const fmtQty = (quantity, code, max = 3) => `${mtrFmtNum(quantity, max)} ${unitInfo(code).label}`;

// Vermogensrij ook als kWh tonen ("128,34 kW = 32,08 kWh"); voor niet-vermogen
// enkel de eigen eenheid.
export function fmtQtyWithKwh(quantity, code, max = 3) {
  const u = unitInfo(code);
  const own = fmtQty(quantity, code, max);
  if (!u.isPower) return own;
  return `${own} = ${mtrFmtNum(Number(quantity || 0) * QUARTER_HOURS, max)} kWh`;
}

// ── Methodes ────────────────────────────────────────────────────────────────────
// Enkel deze methodes zijn "verbruik/injectie" (energie). ConsumedPower en
// AccessCapacity zijn vermogen/capaciteit, de rest is reactief: allemaal andere
// datasets die niet in het verbruiksprofiel thuishoren.
// Verbruik en productie worden BEWUST apart gehouden: het zijn tegengestelde
// stromen, dus één totaal eroverheen ("netto") zou een ander getal zijn dan wat de
// pagina belooft. Vandaar aparte totalen "Totaal verbruik" en "Totaal productie".
export const CONSUMPTION_METHODS = ['ActiveConsumption'];
export const PRODUCTION_METHODS = ['ActiveProduction'];
export const ENERGY_METHODS = [...CONSUMPTION_METHODS, ...PRODUCTION_METHODS];
export const isEnergyMethod = (m) => ENERGY_METHODS.includes(String(m || ''));
export const hasMethod = (rows, methods) => (rows || []).some((e) => methods.includes(String(e.meteringMethod || '')));

// ── Rectificaties ───────────────────────────────────────────────────────────────
// Sleutel waarop een correctie en het origineel elkaar overlappen. Status en id
// horen er BEWUST niet in: dat zijn juist de velden die verschillen.
export const rectKey = (e) => [e.fromUTC, e.toUTC, e.meteringMethod, e.timeframe, e.unit].join('|');

// Houd per sleutel de rij met de nieuwste validFromUTC over. Gelijkspel → de
// niet-Rectified rij, daarna het hoogste id, zodat de uitkomst deterministisch is.
export function dedupeNewest(entities) {
  const best = new Map();
  for (const e of entities || []) {
    const k = rectKey(e);
    const cur = best.get(k);
    if (!cur || isNewer(e, cur)) best.set(k, e);
  }
  const rows = [...best.values()];
  return { rows, superseded: (entities || []).length - rows.length };
}

function isNewer(a, b) {
  const va = String(a.validFromUTC || ''), vb = String(b.validFromUTC || '');
  if (va !== vb) return va > vb;
  const ra = a.status === 'Rectified', rb = b.status === 'Rectified';
  if (ra !== rb) return rb;                       // niet-Rectified wint
  return (Number(a.id) || 0) > (Number(b.id) || 0);
}

// ── Tijdvakken (Belgische lokale tijd) ─────────────────────────────────────────
// De dagrijen van Lynx lopen 22:00Z→22:00Z: dat is lokaal middernacht→middernacht.
// Bucketen op UTC zou die waarden een dag verkeerd plaatsen, dus we bucketen op
// Europe/Brussels.
const BE_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' });
const BE_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hour12: false });

export const beDayKey = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : BE_DAY.format(d); };
export const beMonthKey = (iso) => beDayKey(iso).substring(0, 7);
// `hour12: false` levert in sommige ICU-versies "24:00" i.p.v. "00:00" voor
// middernacht. Dat zou een kwartiersleutel opleveren die nergens matcht, dus
// normaliseren we het hier.
export const beTimeKey = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : BE_TIME.format(d).replace(/^24:/, '00:'); };

export const bucketKey = (iso, granularity) => granularity === 'quarter' ? beTimeKey(iso)
  : granularity === 'day' ? beDayKey(iso)
    : beMonthKey(iso);

// Begin (UTC) van de lokale dag ná het gegeven tijdstip.
// DST-bestendig: lokale middernacht ligt 1 uur (CET) of 2 uur (CEST) vóór
// UTC-middernacht, en op de wisseldagen is dat NIET hetzelfde als de offset later op
// die dag. Daarom proberen we beide kandidaten en houden we die waarvan de Brusselse
// tijd écht 00:00 op de gevraagde datum is — dan klopt ook 30/03 (23 uur) en
// 26/10 (25 uur).
// Brusselse middernacht (UTC-ms) van een lokale dag 'YYYY-MM-DD'.
export function localDayStartMs(day) {
  const [y, m, d] = String(day).split('-').map(Number);
  const target = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  for (const off of [2, 1]) {
    const t = Date.UTC(y, m - 1, d, -off);
    const iso = new Date(t).toISOString();
    if (beDayKey(iso) === target && beTimeKey(iso) === '00:00') return t;
  }
  return Date.UTC(y, m - 1, d, -1);                          // vangnet
}
function nextLocalDayStartMs(ms) {
  const [y, m, d] = beDayKey(new Date(ms).toISOString()).split('-').map(Number);
  const nx = new Date(Date.UTC(y, m - 1, d + 1));            // normaliseert maand/jaar
  return localDayStartMs(nx.toISOString().substring(0, 10));
}

// Het ophaalvenster voor een reeks LOKALE dagen, uitgedrukt in UTC zoals de API het
// verwacht: van Brusselse middernacht op `fromDay` tot één seconde vóór Brusselse
// middernacht ná `toDay`.
//
// WAAROM NIET GEWOON 'YYYY-MM-DDT00:00:00Z'…'T23:59:59Z': de API geeft elke meting
// terug die het venster OVERLAPT, en een dagmeting loopt in de zomer van 22:00Z tot
// 22:00Z. Een UTC-dag raakt dus altijd TWEE Brusselse dagen, en de kwartiertabel en
// het kengetal "Verbruik totaal" telden voor één gekozen dag prompt 192 kwartieren
// (gemeten 10-09-2026: venster 01/07 00:00Z–23:59:59Z gaf de dagrijen van 1 én 2
// juli terug; venster 30/06 22:00Z–01/07 22:00Z gaf er precies één).
const isoNoMs = (ms) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
export function localDayWindowUtc(fromDay, toDay = fromDay) {
  const from = localDayStartMs(fromDay);
  const to = nextLocalDayStartMs(localDayStartMs(toDay)) - 1000;
  return { fromUtc: isoNoMs(from), toUtc: isoNoMs(to) };
}

// Valt een tijdstip binnen het gekozen venster van LOKALE dagen? Zonder venster telt
// alles. Dit is het vangnet naast het ophaalvenster: ook als de API tóch een meting
// van de dag ernaast meestuurt, komt geen enkel kwartier van buiten de periode in de
// grafiek, het kengetal of de kwartiertabel terecht.
export const inClipDay = (clip, iso) => {
  if (!clip || (!clip.fromDay && !clip.toDay)) return true;
  const dag = beDayKey(iso);
  if (!dag) return false;
  return (!clip.fromDay || dag >= String(clip.fromDay).substring(0, 10)) && (!clip.toDay || dag <= String(clip.toDay).substring(0, 10));
};

// Verdeel de periode van een meting over de tijdvakken die ze overlapt, met per
// tijdvak het AANDEEL (naar tijdsduur, dus DST- en maandlengte-bestendig).
// Nodig voor gas: een meterstand loopt daar vaak over meerdere maanden of zelfs een
// heel jaar (bv. 01/06/2025 → 01/06/2026), en zonder verdeling zou die nergens in
// een maandoverzicht verschijnen.
export function bucketSlices(row, granularity) {
  const a = new Date(row.fromUTC).getTime();
  const b = new Date(row.toUTC).getTime();
  if (!(b > a)) return [];
  const total = b - a;
  const slices = new Map();
  let cursor = a;
  // Ruime beveiliging tegen een onmogelijke periode (±10 jaar aan dagen).
  for (let guard = 0; cursor < b && guard < 4000; guard++) {
    const dayKey = beDayKey(new Date(cursor).toISOString());
    const end = Math.min(nextLocalDayStartMs(cursor), b);
    if (!(end > cursor)) break;
    const key = granularity === 'month' ? dayKey.substring(0, 7) : dayKey;
    slices.set(key, (slices.get(key) || 0) + (end - cursor));
    cursor = end;
  }
  return [...slices.entries()].map(([key, ms]) => ({ key, fraction: ms / total }));
}

// Het tijdvak waar een meting overwegend in valt, of null als ze echt verdeeld moet
// worden. Nodig omdat de netbeheerder een "maandstand" niet op lokale middernacht
// laat beginnen maar bv. van 01/01 05:00Z tot 01/02 05:00Z: strikt gezien twee
// maanden, in de praktijk januari. Zonder deze tolerantie zou élke maandstand
// verdeeld worden en zou geen enkele maand nog gelijk zijn aan de meterstand die de
// netbeheerder doorgaf. Vanaf 90% van de tijdsduur in één tijdvak snappen we erop.
export function dominantBucket(row, granularity, minFraction = 0.9) {
  const slices = bucketSlices(row, granularity);
  if (!slices.length) return null;
  const top = slices.reduce((a, b) => (b.fraction > a.fraction ? b : a));
  return top.fraction >= minFraction ? top.key : null;
}

// Grenzen van het gevraagde venster, uitgedrukt in bucket-sleutels. Verdeelde
// tijdvakken buiten dat venster horen niet in beeld: een jaarstand die tot juni 2026
// loopt mag in de weergave "2025" geen maanden van 2026 toevoegen.
//
// `clip` bevat LOKALE datums (YYYY-MM-DD), niet UTC-tijdstippen: het venster dat de
// gebruiker koos is "december 2025", niet "tot 31/12 23:59 UTC" — dat laatste is in
// Brusselse tijd al 1 januari en zou er een maand bij tonen.
function clipBounds(clip, granularity) {
  if (!clip || !clip.fromDay || !clip.toDay || granularity === 'quarter') return null;
  const cut = granularity === 'month' ? 7 : 10;
  return { from: String(clip.fromDay).substring(0, cut), to: String(clip.toDay).substring(0, cut) };
}
const inBounds = (bounds, key) => !bounds || (key >= bounds.from && key <= bounds.to);

// Valt de hele periode van een rij binnen één bucket? Zo niet (bv. een maandrij bij
// dagbuckets) kunnen we ze niet toewijzen zonder te verzinnen hoe ze verdeeld is.
export function spanBucket(row, granularity) {
  const a = bucketKey(row.fromUTC, granularity);
  // De einddatum is exclusief: een dagrij 01/06 22:00Z → 02/06 22:00Z eindigt op de
  // grens van de volgende bucket. Neem daarom één minuut vóór het einde.
  const endIso = new Date(new Date(row.toUTC).getTime() - 60000).toISOString();
  const b = bucketKey(endIso, granularity);
  return a && a === b ? a : null;
}

// ── Kwartierwaarden ─────────────────────────────────────────────────────────────
// Elke reading met zijn meting erbij, zodat een tabel eenheid/methode/kwaliteit kan
// tonen zonder terug te moeten zoeken.
// `clip` = het gekozen venster van lokale dagen (zie inClipDay): kwartieren van een
// dag erbuiten horen niet in de tabel.
export function readingRows(rows, { clip } = {}) {
  const out = [];
  for (const e of rows || []) {
    for (const r of e.readings || []) {
      if (!inClipDay(clip, r.fromUTC)) continue;
      out.push({
        fromUTC: r.fromUTC, toUTC: r.toUTC, quantity: Number(r.quantity) || 0, quality: r.quality || '—',
        unit: e.unit, meteringMethod: e.meteringMethod, timeframe: e.timeframe, category: e.category,
        status: e.status, entityId: e.id, meteringContext: e.meteringContext,
      });
    }
  }
  return out.sort((a, b) => String(a.fromUTC).localeCompare(String(b.fromUTC)) || String(a.meteringMethod).localeCompare(String(b.meteringMethod)));
}

// ── Dag- en maandwaarden ────────────────────────────────────────────────────────
// Aggregeert PER (bucket, methode, eenheid) — nooit over eenheden heen. Bron per
// rij: 'kwartier' (opgeteld uit readings) of 'periode' (de meting zelf, alleen als
// ze volledig in één bucket valt). Rijen die geen bucket halen worden geteld in
// `skipped`, zodat de UI kan zeggen wat er niet in de tabel staat.
// `distribute` (standaard aan voor dag/maand): een meting die meer dan één tijdvak
// beslaat wordt pro rata over die tijdvakken verdeeld, met bron 'verdeeld' zodat een
// schatting nooit als een echte meterstand te lezen is. Zonder verdeling zou een
// gas-jaarstand in geen enkel maandoverzicht opduiken. Bij kwartierbuckets verdelen
// we NIET: een kwartierprofiel uit een jaarstand afleiden is zinloos.
export function aggregateBuckets(rows, granularity, { distribute = granularity !== 'quarter', clip } = {}) {
  const bounds = clipBounds(clip, granularity);
  const map = new Map();
  const skipped = [];
  const add = (key, method, unit, timeframe, source, quantity) => {
    const k = `${key}|${method}|${unit}|${source}`;
    const cur = map.get(k) || { key, meteringMethod: method, unit, timeframe, source, quantity: 0, count: 0 };
    cur.quantity += Number(quantity) || 0;
    cur.count += 1;
    if (cur.timeframe !== timeframe) cur.timeframe = 'meerdere';
    map.set(k, cur);
  };
  for (const e of rows || []) {
    const readings = e.readings || [];
    if (readings.length) {
      for (const r of readings) {
        if (!inClipDay(clip, r.fromUTC)) continue;
        const k = bucketKey(r.fromUTC, granularity);
        if (inBounds(bounds, k)) add(k, e.meteringMethod, e.unit, e.timeframe, 'kwartier', r.quantity);
      }
      continue;
    }
    const b = dominantBucket(e, granularity);
    if (b) {
      if (inBounds(bounds, b)) add(b, e.meteringMethod, e.unit, e.timeframe, 'periode', e.quantity);
      continue;
    }
    if (!distribute) { skipped.push(e); continue; }
    const slices = bucketSlices(e, granularity);
    if (!slices.length) { skipped.push(e); continue; }
    for (const s of slices) {
      if (!inBounds(bounds, s.key)) continue;
      add(s.key, e.meteringMethod, e.unit, e.timeframe, 'verdeeld', (Number(e.quantity) || 0) * s.fraction);
    }
  }
  return {
    rows: [...map.values()].sort((a, b) => String(a.key).localeCompare(String(b.key))
      || String(a.meteringMethod).localeCompare(String(b.meteringMethod))
      || String(a.unit).localeCompare(String(b.unit))),
    skipped,
  };
}

// ── Verbruiksprofiel (grafiek) ──────────────────────────────────────────────────
// Uitsluitend energie, alles in kWh:
//   • enkel ActiveConsumption/ActiveProduction (geen reactief, geen capaciteit)
//   • kwartierwaarden in kW worden ÷4 naar kWh
//   • een periodemeting (maandstand) telt ALLEEN mee als er voor die methode geen
//     kwartierdata in die periode is — anders zou dezelfde energie dubbel tellen
// Levert punten per bucket plus wat er is overgeslagen/omgerekend, zodat de pagina
// dat kan verantwoorden in plaats van stil een keuze te maken.
// `methods` beperkt de reeks tot één stroomrichting: CONSUMPTION_METHODS voor
// verbruik, PRODUCTION_METHODS voor productie. Standaard beide (voor code die geen
// onderscheid nodig heeft).
export function energySeries(rows, granularity, methods = ENERGY_METHODS, { distribute = granularity !== 'quarter', clip } = {}) {
  const bounds = clipBounds(clip, granularity);
  const energy = (rows || []).filter((e) => methods.includes(String(e.meteringMethod || '')));
  const interval = energy.filter((e) => (e.readings || []).length > 0);
  const periods = energy.filter((e) => (e.readings || []).length === 0);

  // Tijdsbereik dat door kwartierdata gedekt is, per methode.
  const covered = new Map();
  for (const e of interval) {
    const list = covered.get(e.meteringMethod) || [];
    list.push([new Date(e.fromUTC).getTime(), new Date(e.toUTC).getTime()]);
    covered.set(e.meteringMethod, list);
  }
  const isCovered = (e) => {
    const a = new Date(e.fromUTC).getTime(), b = new Date(e.toUTC).getTime();
    return (covered.get(e.meteringMethod) || []).some(([x, y]) => x < b && y > a);
  };

  const buckets = new Map();
  let outsideWindowKwh = 0;
  const add = (key, kwh) => {
    if (!key) return;
    if (!inBounds(bounds, key)) { outsideWindowKwh += kwh; return; }
    buckets.set(key, (buckets.get(key) || 0) + kwh);
  };
  let converted = 0, excludedCovered = 0, excludedSpan = 0, excludedUnit = 0, excludedSpanKwh = 0;
  let distributedRows = 0, distributedKwh = 0;

  for (const e of interval) {
    const u = unitInfo(e.unit);
    if (u.kind !== 'power' && u.kind !== 'energy') { excludedUnit += 1; continue; }
    for (const r of e.readings || []) {
      const { value, converted: conv } = toKwh(r.quantity, e.unit);
      if (value == null) continue;
      // Kwartier van een dag buiten het venster: telt als "buiten beeld", niet als
      // deel van de periode. Bij kwartierbuckets (weergave Dag) is dit de ENIGE
      // afkapping — de sleutel "HH:MM" draagt de dag niet.
      if (!inClipDay(clip, r.fromUTC)) { outsideWindowKwh += value; continue; }
      if (conv) converted += 1;
      add(bucketKey(r.fromUTC, granularity), value);
    }
  }
  for (const e of periods) {
    const u = unitInfo(e.unit);
    if (u.kind !== 'power' && u.kind !== 'energy') { excludedUnit += 1; continue; }
    if (isCovered(e)) { excludedCovered += 1; continue; }
    const b = dominantBucket(e, granularity);
    const { value, converted: conv } = toKwh(e.quantity, e.unit);
    if (value == null) continue;
    if (b) {
      if (conv) converted += 1;
      add(b, value);
      continue;
    }
    // Beslaat meerdere tijdvakken (bij gas is een jaarstand normaal). Pro rata naar
    // tijdsduur verdelen, want anders zou deze energie in geen enkel maandoverzicht
    // verschijnen. Het aandeel wordt apart geteld zodat de pagina kan zeggen dat een
    // deel van de balken een verdeling is en geen echte meterstand.
    if (distribute) {
      const slices = bucketSlices(e, granularity);
      if (slices.length) {
        if (conv) converted += 1;
        let binnen = 0;
        for (const s of slices) { add(s.key, value * s.fraction); if (inBounds(bounds, s.key)) binnen += value * s.fraction; }
        distributedRows += 1;
        distributedKwh += binnen;      // enkel het deel dat in beeld is
        continue;
      }
    }
    excludedSpan += 1;
    excludedSpanKwh += value;
  }

  const points = [...buckets.entries()]
    .map(([key, value]) => ({ key, value: Math.round(value * 1000) / 1000 }))
    .sort((a, b) => a.key.localeCompare(b.key));
  return {
    points,
    totalKwh: Math.round(points.reduce((s, p) => s + p.value, 0) * 1000) / 1000,
    convertedReadings: converted,
    excludedCovered,     // maandstanden overgeslagen omdat er kwartierdata is
    excludedSpan,        // periodes die niet in één bucket vallen
    excludedSpanKwh: Math.round(excludedSpanKwh * 1000) / 1000,   // hun volume, apart
    distributedRows,     // metingen die pro rata over de tijdvakken zijn verdeeld
    distributedKwh: Math.round(distributedKwh * 1000) / 1000,
    // Volume dat buiten het gevraagde venster viel (een meterstand kan doorlopen tot
    // ná de gekozen periode). Hoort niet in de balken, maar wel in de verantwoording.
    outsideWindowKwh: Math.round(outsideWindowKwh * 1000) / 1000,
    excludedUnit,        // rijen met een eenheid die geen energie kan zijn
    nonEnergyRows: (rows || []).length - energy.length,
  };
}

// ── Datasets ────────────────────────────────────────────────────────────────────
// Een GSRN levert meerdere ONAFHANKELIJKE datasets naast elkaar. Voorbeeld uit de
// echte data (GSRN …317688, 2025):
//   CMSAggregation | ActiveConsumption | KWH  →  3.746 kWh   (maandaggregatie)
//   CMSAggregation | ActiveProduction  | KWH  →  5.025 kWh
//   CMSAggregation | ConsumedPower     | KWT  →     94 kW
//   Ediel          | ActiveConsumption | KWT  →  9.027 kW    (kwartierprofiel)
//   Ediel          | ActiveProduction  | KWT  → 12.075 kW
// De eerste en de vierde beschrijven hetzelfde verbruik in dezelfde periode, maar
// uit een andere bron en in een andere eenheid. Categorie, methode en eenheid mogen
// daarom NOOIT samengeteld worden — ook niet na omrekening. Elke combinatie is een
// eigen reeks met een eigen totaal.
export const datasetKey = (e) => `${e.category || '—'}|${e.meteringMethod || '—'}|${String(e.unit || '—').toUpperCase()}`;

export function splitDatasets(rows, methods = null) {
  const map = new Map();
  for (const e of rows || []) {
    if (methods && !methods.includes(String(e.meteringMethod || ''))) continue;
    const k = datasetKey(e);
    if (!map.has(k)) map.set(k, { key: k, category: e.category || '—', method: e.meteringMethod || '—', unit: String(e.unit || '—').toUpperCase(), rows: [] });
    map.get(k).rows.push(e);
  }
  return [...map.values()].sort((a, b) =>
    a.method.localeCompare(b.method) || a.category.localeCompare(b.category) || a.unit.localeCompare(b.unit));
}

// Eén reeks per dataset. Binnen een dataset gelden de gewone regels (kwartierdata
// wint van een periodestand over dezelfde periode, kW ÷4 naar kWh, pro rata
// verdelen, afkappen op het venster); tússen datasets wordt niets samengevoegd.
export function energyDatasets(rows, granularity, methods = ENERGY_METHODS, opts = {}) {
  return splitDatasets(rows, methods).map((d) => ({ ...d, series: energySeries(d.rows, granularity, methods, opts) }));
}

// Alle kwartierbuckets van een dag, ook de lege — een profiel met gaten moet als
// gat te zien zijn en niet stil ingedikt worden.
export function fillQuarters(points) {
  const map = new Map(points.map((p) => [p.key, p.value]));
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const key = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      out.push({ key, value: map.get(key) ?? 0 });
    }
  }
  return out;
}

// Alle 96 kwartiersleutels van een dag, in volgorde. Gedeeld met fillQuarters,
// zodat een dagoverzicht over meerdere aansluitingen dezelfde x-as houdt.
export const QUARTER_KEYS = fillQuarters([]).map((p) => p.key);

// ── Eén getal per stroomrichting (overzichtsweergave) ───────────────────────────
// De detailtabellen tonen élke dataset apart, want dezelfde energie komt soms uit
// twee bronnen tegelijk (een Ediel-kwartierprofiel én een CMS-maandaggregatie voor
// dezelfde maand). Voor kengetallen en de overzichtsgrafiek is één getal per
// stroomrichting nodig, en die datasets optellen zou dubbeltellen. We KIEZEN er dus
// één en zetten de andere expliciet opzij, zodat de pagina kan tonen wat er niet in
// het kengetal zit in plaats van er stil van af te wijken.
//
// Keuzevolgorde, van zwaarst naar lichtst:
//   1. het GROOTSTE VOLUME in het venster. De kandidaten beschrijven per definitie
//      dezelfde stroomrichting van dezelfde aansluiting, dus de grootste beschrijft
//      het meest van de periode; een kleinere kiezen zou het kengetal stil te laag
//      maken. Optellen is geen alternatief — dat telt dezelfde energie dubbel.
//   2. de dekking (aantal tijdvakken met een waarde), voor het geval twee bronnen
//      op hetzelfde volume uitkomen;
//   3. de fijnste bron (heeft kwartierwaarden);
//   4. alfabetisch op sleutel, zodat dezelfde invoer altijd hetzelfde resultaat geeft.
//
// NB: "meeste tijdvakken" als eerste criterium zou verkeerd uitpakken. Een
// maandstand die pro rata over 30 dagen verdeeld wordt, "dekt" 30 tijdvakken, ook
// als het maar één gemeten getal is — dan zou een schatting het winnen van een echt
// gemeten profiel puur omdat ze uitgesmeerd is.
export function pickPrimaryDataset(datasets) {
  const list = datasets || [];
  if (!list.length) return null;
  const covered = (d) => (d.series?.points || []).filter((p) => p.value).length;
  const interval = (d) => ((d.rows || []).some((e) => (e.readings || []).length > 0) ? 1 : 0);
  const total = (d) => d.series?.totalKwh || 0;
  return [...list].sort((a, b) =>
    total(b) - total(a) || covered(b) - covered(a) || interval(b) - interval(a) || String(a.key).localeCompare(String(b.key)))[0];
}

// Verbruik (afname) en productie (injectie) van ÉÉN aansluiting over het venster,
// in kWh en met dezelfde tijdvakken als de grafiek. De twee stromen blijven
// gescheiden: het zijn tegengestelde richtingen, dus één totaal eroverheen zou een
// ander getal zijn dan wat de kengetallen beloven.
export function siteFlows(rows, granularity, opts = {}) {
  const build = (methods) => {
    const sets = energyDatasets(rows, granularity, methods, opts);
    const primary = pickPrimaryDataset(sets);
    return {
      primary,
      points: primary ? primary.series.points : [],
      totalKwh: primary ? primary.series.totalKwh : 0,
      // De datasets die niet gekozen zijn — met hun volume, zodat de pagina kan
      // zeggen wélke bron buiten het kengetal blijft.
      setAside: sets.filter((d) => d !== primary).map((d) => ({ key: d.key, label: datasetLabel(d), totalKwh: d.series.totalKwh })),
      // Rijen met een eenheid die geen energie kan zijn (bv. gas in m³ zonder
      // calorische waarde): niet omgerekend, wel geteld.
      excludedUnit: primary ? primary.series.excludedUnit : 0,
      distributedKwh: primary ? primary.series.distributedKwh : 0,
    };
  };
  return { verbruik: build(CONSUMPTION_METHODS), productie: build(PRODUCTION_METHODS) };
}

const r3 = (v) => Math.round((Number(v) || 0) * 1000) / 1000;

// ── Kwartierdetail met volledige tijdstempel ────────────────────────────────────
// De grafiek werkt met de tijd VAN DE DAG (`granularity: 'quarter'` → 00:00…23:45),
// want ze toont één dag. Een export over meerdere dagen kan daar niet mee: 1 juni
// 12:00 en 2 juni 12:00 zouden op dezelfde regel vallen. Deze functie levert daarom
// per meting het volledige UTC-tijdstip, zodat er een prijs aan te koppelen valt.
//
// De datasetregel blijft onverkort gelden: per stroomrichting wint ÉÉN dataset
// (dezelfde weging als de grafiek, via pickPrimaryDataset) en alleen díé telt mee.
// Wél kiezen we hier uitsluitend uit datasets die ÉCHTE kwartiermetingen bevatten.
// Dat wijkt bewust af van de grafiek: daar mag een maandstand van de kwartierreeks
// winnen omdat hij de periode vollediger beschrijft, maar in een kwartierexport
// heeft zo'n stand niets te zoeken. Hem pro rata uitsmeren zou een profiel
// verzinnen dat de meter nooit gemeten heeft — en daar zou dan ook nog een
// kwartierprijs aan hangen. Geen kwartierdata betekent dus: geen regels.
export function quarterDetail(rows, methods, { clip } = {}) {
  const sets = energyDatasets(rows, 'day', methods)
    .filter((d) => (d.rows || []).some((e) => (e.readings || []).length > 0));
  const primary = pickPrimaryDataset(sets);
  if (!primary) return [];
  const som = new Map();
  for (const e of primary.rows || []) {
    const u = unitInfo(e.unit);
    if (u.kind !== 'power' && u.kind !== 'energy') continue;
    for (const r of e.readings || []) {
      if (!r.fromUTC) continue;
      const dag = beDayKey(r.fromUTC);
      if (clip && ((clip.fromDay && dag < clip.fromDay) || (clip.toDay && dag > clip.toDay))) continue;
      const { value } = toKwh(r.quantity, e.unit);
      if (value == null) continue;
      som.set(r.fromUTC, (som.get(r.fromUTC) || 0) + value);
    }
  }
  return [...som.entries()]
    .map(([fromUTC, kwh]) => ({ fromUTC, kwh: r3(kwh) }))
    .sort((a, b) => String(a.fromUTC).localeCompare(String(b.fromUTC)));
}

// ── Verkoopprijs per kwartier ───────────────────────────────────────────────────
// Uit de spotprijs en de contractvoorwaarden van de factuur, allebei in EUR/MWh —
// dus de som klopt zonder omrekening.
//
//   afname   = coeff offtake   × spot + majoratie
//   injectie = coeff injection × spot − majoratie
//
// LET OP — de dubbele min. `elMajorationInjection` staat in de data NEGATIEF
// (−20): het veld draagt zelf al het teken van een aftrek. Wie dan letterlijk
// `− maj` rekent, telt op: spot − (−20) = spot + 20. Daarom nemen we hier de
// GROOTTE van de majoratie en trekken die af. Verander dit niet terug naar een
// kale `- tarief.majInjection` omdat "de formule dat zegt": de formule beschrijft
// het bedrag, niet het teken waarmee het toevallig opgeslagen is. De testen
// hieronder in metering.test.js leggen beide gevallen vast.
//
// Zonder tarief of zonder spot geven we null in plaats van 0 — een prijs van nul
// is een bewering, een lege cel niet. Staat er voor een richting geen enkele
// voorwaarde op de factuur (coëfficiënt én majoratie nul), dan is er voor die
// richting geen contract en blijft de cel eveneens leeg; anders zou een
// aansluiting zonder injectiecontract een keurige injectieprijs van 0,00 tonen.
//
// ZONDER COËFFICIËNT IS ER GEEN PRIJS. Een factuur die wél een majoratie draagt
// maar geen coëfficiënt levert `0 × marktprijs + majoratie` — een prijs die de
// markt niet volgt. Gemeten geval: een kwartierexport van een heel jaar waarin
// élke regel 15 EUR/MWh stond (de majoratie) terwijl de marktprijs ernaast van
// 6 tot 460 EUR/MWh liep. Zo'n getal is niet "ongeveer goed", het is de prijs
// van een ander contract. Beter een lege cel: die stelt de vraag, het getal niet.
export function verkoopprijs(tarief, spot, richting) {
  const s = Number(spot);
  if (tarief == null || spot == null || !Number.isFinite(s)) return null;
  if (richting === 'injectie') {
    if (!tarief.coeffInjection) return null;
    return tarief.coeffInjection * s - Math.abs(tarief.majInjection);
  }
  if (!tarief.coeffOfftake) return null;
  return tarief.coeffOfftake * s + tarief.majOfftake;
}

// Het tijdvak met de hoogste waarde, of null als er niets is. Nul telt niet als
// piek: een lege periode heeft geen piekdag.
export function peakBucket(points) {
  let best = null;
  for (const p of points || []) if (p.value > 0 && (!best || p.value > best.value)) best = { key: p.key, value: r3(p.value) };
  return best;
}

// Alle geselecteerde aansluitingen samen: per tijdvak verbruik, productie en netto
// (verbruik − productie). Optellen mag hier wél — het zijn verschillende
// aansluitingen, dus verschillende energie; binnen één aansluiting is er per stroom
// al tot één dataset teruggebracht.
// `quarters` vult de 96 kwartieren van een dag aan, zodat een gat in het profiel
// als gat zichtbaar blijft in plaats van stil ingedikt te worden.
export function combineSites(sites, { quarters = false } = {}) {
  const list = sites || [];
  const idx = list.map((s) => ({
    site: s,
    v: new Map((s.flows?.verbruik.points || []).map((p) => [p.key, p.value])),
    p: new Map((s.flows?.productie.points || []).map((p) => [p.key, p.value])),
  }));
  let keys;
  if (quarters) keys = QUARTER_KEYS;
  else {
    const set = new Set();
    for (const i of idx) { for (const k of i.v.keys()) set.add(k); for (const k of i.p.keys()) set.add(k); }
    keys = [...set].sort((a, b) => a.localeCompare(b));
  }
  // `data`: er is voor dit tijdvak een verbruiksmeting, óók als die 0 kWh is. Een
  // gemeten nul is geen ontbrekend kwartier: het gemiddelde en "N kwartieren met
  // data" rekenen ze mee. Alleen bij `quarters` bestaan er rijen zónder meting
  // (de 96 vaste sleutels); elders komt elke sleutel uit een meting.
  const rows = keys.map((key) => {
    let verbruik = 0, productie = 0, data = false;
    for (const i of idx) { verbruik += i.v.get(key) || 0; productie += i.p.get(key) || 0; if (i.v.has(key)) data = true; }
    return { key, verbruik: r3(verbruik), productie: r3(productie), netto: r3(verbruik - productie), data };
  });
  const perSite = list.map((s) => {
    const verbruik = s.flows?.verbruik.totalKwh || 0;
    const productie = s.flows?.productie.totalKwh || 0;
    return {
      gsrn: s.gsrn, market: s.market, address: s.address || '', name: s.name || '',
      verbruik: r3(verbruik), productie: r3(productie), netto: r3(verbruik - productie),
      peak: peakBucket(s.flows?.verbruik.points),
      setAside: [...(s.flows?.verbruik.setAside || []), ...(s.flows?.productie.setAside || [])],
      excludedUnit: (s.flows?.verbruik.excludedUnit || 0) + (s.flows?.productie.excludedUnit || 0),
      distributedKwh: r3((s.flows?.verbruik.distributedKwh || 0) + (s.flows?.productie.distributedKwh || 0)),
    };
  }).sort((a, b) => b.verbruik - a.verbruik || String(a.gsrn).localeCompare(String(b.gsrn)));
  const totals = perSite.reduce((acc, s) => ({
    verbruik: r3(acc.verbruik + s.verbruik),
    productie: r3(acc.productie + s.productie),
    netto: r3(acc.netto + s.netto),
    setAside: acc.setAside + s.setAside.length,
    excludedUnit: acc.excludedUnit + s.excludedUnit,
    distributedKwh: r3(acc.distributedKwh + s.distributedKwh),
  }), { verbruik: 0, productie: 0, netto: 0, setAside: 0, excludedUnit: 0, distributedKwh: 0 });
  return { rows, perSite, totals, peak: peakBucket(rows.map((r) => ({ key: r.key, value: r.verbruik }))) };
}

// De zwaarste tijdvakken (topdagen/-maanden/-kwartieren). Lege tijdvakken vallen
// weg: die horen niet in een "top".
export function topBuckets(rows, n = 5, field = 'verbruik') {
  return [...(rows || [])]
    .filter((r) => (r[field] || 0) > 0)
    .sort((a, b) => b[field] - a[field] || String(a.key).localeCompare(String(b.key)))
    .slice(0, n);
}

// Procentueel verschil t.o.v. de vorige periode. null als er geen vergelijkbare
// vorige waarde is — dan toont de UI geen pijltje in plaats van "+100%".
export const pctDelta = (current, previous) => {
  const p = Number(previous) || 0;
  if (!p) return null;
  return ((Number(current) || 0) - p) / p * 100;
};

// Metingen gegroepeerd per categorie (Ediel / EdielHistorical / CMSAggregation …),
// grootste groep eerst — de pagina maakt hier één tabel per categorie van.
export function splitByCategory(rows) {
  const map = new Map();
  for (const e of rows || []) {
    const k = e.category || '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(e);
  }
  return [...map.entries()]
    .map(([category, list]) => ({ category, rows: list }))
    .sort((a, b) => b.rows.length - a.rows.length || a.category.localeCompare(b.category));
}

// Totaal per eenheid binnen een set rijen — voor koppen/kengetallen waar de som
// over eenheden zinloos zou zijn.
export function totalsByUnit(rows) {
  const map = new Map();
  for (const e of rows || []) {
    const k = String(e.unit || '—').toUpperCase();
    const cur = map.get(k) || { unit: k, quantity: 0, count: 0 };
    cur.quantity += Number(e.quantity) || 0;
    cur.count += 1;
    map.set(k, cur);
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

// ── Is er een injectieprijs om te tonen? ───────────────────────────────────────
// Bepaalt of de detailexport haar injectiekolommen (marktprijs + verkoopprijs)
// meeneemt. Bij een aansluiting zonder injectiecontract beschrijven die een
// contract dat niet bestaat: lege kolommen waarin de lezer gaat zoeken naar iets
// wat er niet is.
//
// De toets staat bewust op de PRIJS en niet op de gemeten productie. Die twee
// lopen niet gelijk, en beide richtingen komen echt voor:
//   - productie zonder injectiecontract (gemeten op een echte aansluiting): de
//     kWh staan in de kolom "Productie", maar er is geen injectieprijs — dan zijn
//     de prijskolommen alsnog leeg en horen ze weg te blijven;
//   - een injectiecontract zonder productie in het gekozen venster: de prijs
//     bestaat wél (index of spot × coëfficiënt), dus de kolommen blijven staan.
//     Anders lijkt het contract verdwenen zodra iemand een periode kiest waarin de
//     zon niets deed.
export function heeftInjectiePrijs(rijen) {
  return (rijen || []).some((r) => r?.basisInjectie != null || r?.prijsInjectie != null);
}
